import type { UserUiSettings, UserWorkspacePayload } from './auth.js';

type SiteRecord = {
  id: string;
  title?: string;
  createdAt?: string;
  lifecycle?: string;
  [key: string]: unknown;
};

type DiagramBundle = { document: unknown; versions: unknown };

function asSites(value: unknown): SiteRecord[] {
  if (!Array.isArray(value)) return [];
  return value.filter((s): s is SiteRecord => Boolean(s && typeof s === 'object' && 'id' in s));
}

export function workspaceDocUpdatedAt(doc: unknown): string {
  if (doc && typeof doc === 'object' && 'updatedAt' in doc) {
    const ts = (doc as { updatedAt?: unknown }).updatedAt;
    if (typeof ts === 'string') return ts;
  }
  return '';
}

function docName(doc: unknown): string | null {
  if (doc && typeof doc === 'object' && 'name' in doc) {
    const name = (doc as { name?: unknown }).name;
    if (typeof name === 'string' && name.trim()) return name.trim();
  }
  return null;
}

function siteTitle(site: SiteRecord): string {
  const title = typeof site.title === 'string' ? site.title.trim() : '';
  if (title && title !== site.id) return title;
  return title || site.id;
}

function mergeSites(local: SiteRecord[], remote: SiteRecord[]): SiteRecord[] {
  const byId = new Map<string, SiteRecord>();
  for (const site of remote) {
    if (site?.id) byId.set(site.id, site);
  }
  for (const site of local) {
    if (!site?.id) continue;
    const existing = byId.get(site.id);
    if (!existing) {
      byId.set(site.id, site);
      continue;
    }
    const merged: SiteRecord = {
      ...existing,
      ...site,
      createdAt: existing.createdAt || site.createdAt,
      lifecycle: site.lifecycle ?? existing.lifecycle,
    };
    merged.title = siteTitle(merged);
    byId.set(site.id, merged);
  }
  return [...byId.values()];
}

function mergeVersions(local: unknown, remote: unknown): unknown {
  const localArr = Array.isArray(local) ? local : [];
  const remoteArr = Array.isArray(remote) ? remote : [];
  if (!localArr.length) return remoteArr;
  if (!remoteArr.length) return localArr;
  const seen = new Set<string>();
  const out: unknown[] = [];
  for (const item of [...localArr, ...remoteArr]) {
    if (!item || typeof item !== 'object') continue;
    const id =
      'id' in item && typeof (item as { id?: unknown }).id === 'string'
        ? (item as { id: string }).id
        : null;
    const key = id ?? JSON.stringify(item);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out.slice(0, 20);
}

function mergeDiagrams(
  local: Record<string, DiagramBundle>,
  remote: Record<string, DiagramBundle>,
): Record<string, DiagramBundle> {
  const out: Record<string, DiagramBundle> = { ...remote };
  for (const [siteId, localBundle] of Object.entries(local)) {
    const remoteBundle = out[siteId];
    if (!remoteBundle?.document) {
      out[siteId] = localBundle;
      continue;
    }
    if (!localBundle?.document) continue;
    const localTs = workspaceDocUpdatedAt(localBundle.document);
    const remoteTs = workspaceDocUpdatedAt(remoteBundle.document);
    const pickLocal = !remoteTs || localTs >= remoteTs;
    out[siteId] = pickLocal
      ? {
          document: localBundle.document,
          versions: mergeVersions(localBundle.versions, remoteBundle.versions),
        }
      : {
          document: remoteBundle.document,
          versions: mergeVersions(localBundle.versions, remoteBundle.versions),
        };
  }
  return out;
}

function sitesFromDiagrams(
  sites: SiteRecord[],
  diagrams: Record<string, DiagramBundle>,
): SiteRecord[] {
  const byId = new Map(sites.map((s) => [s.id, s]));
  for (const siteId of Object.keys(diagrams)) {
    if (siteId === 'SITE-OFFICE-3F' || byId.has(siteId)) continue;
    const name = docName(diagrams[siteId]?.document);
    byId.set(siteId, {
      id: siteId,
      title: name && name !== siteId ? name : siteId,
      createdAt: workspaceDocUpdatedAt(diagrams[siteId]?.document) || new Date().toISOString(),
      lifecycle: 'draft',
    });
  }
  return [...byId.values()].map((s) => ({ ...s, title: siteTitle(s) }));
}

function mergeDrawingMeta(local: unknown, remote: unknown): unknown {
  if (!remote || typeof remote !== 'object') return local ?? {};
  if (!local || typeof local !== 'object') return remote;
  return { ...(remote as object), ...(local as object) };
}

function mergeSettings(local?: UserUiSettings, remote?: UserUiSettings): UserUiSettings | undefined {
  if (!remote) return local;
  if (!local) return remote;
  return { ...remote, ...local };
}

export function asWorkspacePayload(value: unknown): UserWorkspacePayload | null {
  if (!value || typeof value !== 'object') return null;
  const diagrams = (value as { diagrams?: unknown }).diagrams;
  if (!diagrams || typeof diagrams !== 'object' || Array.isArray(diagrams)) {
    return {
      sites: asSites((value as { sites?: unknown }).sites),
      diagrams: {},
      drawingMeta: (value as { drawingMeta?: unknown }).drawingMeta ?? {},
      settings: (value as { settings?: UserUiSettings }).settings,
      deletedSiteIds: Array.isArray((value as { deletedSiteIds?: unknown }).deletedSiteIds)
        ? ((value as { deletedSiteIds: string[] }).deletedSiteIds.filter((id) => typeof id === 'string'))
        : [],
      updatedAt:
        typeof (value as { updatedAt?: unknown }).updatedAt === 'string'
          ? (value as { updatedAt: string }).updatedAt
          : new Date().toISOString(),
    };
  }
  return value as UserWorkspacePayload;
}

function deletedIds(
  local: UserWorkspacePayload | null | undefined,
  remote: UserWorkspacePayload | null | undefined,
): Set<string> {
  const locallyDeleted = new Set(
    (local?.deletedSiteIds ?? []).filter((id) => typeof id === 'string' && id && id !== 'SITE-OFFICE-3F'),
  );
  const remotelyDeleted = new Set(
    (remote?.deletedSiteIds ?? []).filter((id) => typeof id === 'string' && id && id !== 'SITE-OFFICE-3F'),
  );
  const out = new Set<string>([...locallyDeleted, ...remotelyDeleted]);
  for (const id of remotelyDeleted) {
    if (local?.diagrams?.[id] && !locallyDeleted.has(id)) out.delete(id);
  }
  return out;
}

function applyDeletions(
  payload: UserWorkspacePayload,
  extraDeleted?: Set<string>,
): UserWorkspacePayload {
  const deleted = extraDeleted ?? new Set<string>();
  if (!deleted.size) return payload;
  const diagrams = { ...payload.diagrams };
  for (const id of deleted) delete diagrams[id];
  const sites = asSites(payload.sites).filter((s) => !deleted.has(s.id));
  return {
    ...payload,
    sites: sitesFromDiagrams(sites, diagrams),
    diagrams,
    deletedSiteIds: [...deleted],
  };
}

/** Union incoming + stored workspaces; newer diagram wins per site. Tombstones remove sites. */
export function mergeWorkspaces(
  local: UserWorkspacePayload,
  remote: UserWorkspacePayload | null | undefined,
): UserWorkspacePayload {
  const deleted = deletedIds(local, remote);
  if (!remote?.diagrams || !Object.keys(remote.diagrams).length) {
    return applyDeletions({ ...local, updatedAt: new Date().toISOString() }, deleted);
  }
  const localDiagrams = local.diagrams ?? {};
  const remoteDiagrams = remote.diagrams ?? {};
  if (!Object.keys(localDiagrams).length) {
    return applyDeletions(
      {
        ...remote,
        sites: sitesFromDiagrams(asSites(remote.sites), remoteDiagrams),
        deletedSiteIds: [...deleted],
        updatedAt: new Date().toISOString(),
      },
      deleted,
    );
  }

  const mergedDiagrams = mergeDiagrams(localDiagrams, remoteDiagrams);
  const mergedSites = sitesFromDiagrams(
    mergeSites(asSites(local.sites), asSites(remote.sites)),
    mergedDiagrams,
  );

  return applyDeletions(
    {
      sites: mergedSites,
      diagrams: mergedDiagrams,
      drawingMeta: mergeDrawingMeta(local.drawingMeta, remote.drawingMeta),
      settings: mergeSettings(local.settings, remote.settings),
      deletedSiteIds: [...deleted],
      updatedAt: new Date().toISOString(),
    },
    deleted,
  );
}
